/**
 * MSAL config for Entra ID.
 * Prefer runtime `/api/config/auth` (see App.js) so production does not depend on REACT_APP_* at build time.
 * Optional: REACT_APP_ENTRA_CLIENT_ID + REACT_APP_ENTRA_TENANT_ID for local dev / static tooling.
 */

const envClientId = process.env.REACT_APP_ENTRA_CLIENT_ID?.trim();
const envTenantId = process.env.REACT_APP_ENTRA_TENANT_ID?.trim();

/** @deprecated Use auth config from API at runtime; kept for scripts that import before fetch */
export const isEntraEnabled = !!(envClientId && envTenantId);

/** True when the app runs inside an iframe (e.g. Microsoft Teams tab). */
function embeddedInIframe() {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * @param {{ clientId: string, tenantId: string }} p
 */
export function buildMsalConfig({ clientId, tenantId }) {
  const redirectUri = typeof window !== 'undefined' ? window.location.origin : (process.env.REACT_APP_REDIRECT_URI || 'http://localhost:3006');
  return {
    auth: {
      clientId: clientId || '00000000-0000-0000-0000-000000000000',
      authority: `https://login.microsoftonline.com/${tenantId || 'common'}`,
      redirectUri,
      postLogoutRedirectUri: redirectUri,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: embeddedInIframe(),
    },
  };
}

/** Legacy static config when env vars exist at build time */
export const msalConfig = buildMsalConfig({
  clientId: envClientId || '',
  tenantId: envTenantId || '',
});

export const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'offline_access'],
};

/**
 * Clear MSAL's "an interaction is in progress" flag when it is provably stale.
 *
 * cacheLocation is localStorage so the token survives a reload — but MSAL keeps
 * `msal.<clientId>.interaction.status` in the same store. If a redirect is interrupted (back
 * button, tab closed mid-flow, the app redeployed while the user was at the Microsoft page)
 * that flag is never cleared, and every later loginRedirect throws
 * `interaction_in_progress`. The user is then locked out permanently, because a reload does
 * not clear it either — the only escape is clearing site data by hand.
 *
 * Safe to call once handleRedirectPromise() has settled: at that moment no redirect can
 * legitimately still be in flight, so anything left is leftover state.
 *
 * @returns {number} how many stale keys were removed
 */
export function clearStaleInteraction() {
  let removed = 0;
  for (const store of [localStorage, sessionStorage]) {
    try {
      const keys = [];
      for (let i = 0; i < store.length; i += 1) {
        const k = store.key(i);
        if (k && k.startsWith('msal.') && k.includes('interaction.status')) keys.push(k);
      }
      keys.forEach((k) => { store.removeItem(k); removed += 1; });
    } catch { /* storage unavailable — nothing to clear */ }
  }
  return removed;
}
